// src/composables/useApiMenu.js
import { ref, computed } from 'vue'
import { buildMenuTree, getMenuStats, getMenuPath } from '@/utils/menuUtils'
import { getMenuList } from '@/service/cmsMng/homepageMng/menu.service'
import { getAllMenuList } from '@/service/common.service'

/**
 * 전역 네비게이션(헤더/사이드/빵부르기)에서 사용하는 메뉴 원본
 * - DefaultLayout / MainLayout / useCurrentMenu / Side / TopBar 등에서 사용
 */
const navMenusRaw = ref([])

/**
 * 메뉴 관리 화면(그리드, 검색 등)에서만 사용하는 메뉴 원본
 * - 메뉴관리 페이지 전용
 */
const manageMenusRaw = ref([])

export function useApiMenu() {
  /* --------------------------------
   * 1. 전역 네비용 상태 / 통계
   * -------------------------------- */

  // 네비게이션에 사용하는 기준 배열
  const menusWithLevel = computed(() => navMenusRaw.value)

  // 통계 정보 (메뉴 수, 도메인 수 등)
  const menuStats = computed(() => getMenuStats(menusWithLevel.value))

  // 관리 화면용 목록
  const manageMenus = computed(() => manageMenusRaw.value)

  /** 전역 네비 메뉴 세팅용 (내부 전용) */
  const setNavMenus = (menus) => {
    navMenusRaw.value = Array.isArray(menus) ? menus : []
  }

  /* --------------------------------
   * 2. 로딩 함수
   * -------------------------------- */

  /**
   * [전역 네비용]
   * 도메인 ID로 전체 메뉴 호출 + navMenusRaw 세팅
   * - DefaultLayout / MainLayout 에서만 사용
   */
  const loadAllMenuData = async (dmnId) => {
    try {
      const menuReq = { dmnId }
      const res = await getAllMenuList(menuReq)

      if (res?.result && Array.isArray(res.data)) {
        const mapped = res.data.map((menu) => {
          const rawLink = menu.menuLink || ''
          // 마지막 세그먼트(bbsId, contsId 등)만 추출
          const lastSegment = rawLink.split('/').filter(Boolean).pop() || ''

          let path = rawLink

          if (menu.menuType === 'BBS' && lastSegment) {
            // menuLink가 bbsId 혹은 "/.../bbsId" 여도 마지막 세그먼트만 사용
            // board 라우팅이 query 기반이므로 bbsId를 쿼리로 전달
            path = `/board-mng/board/list/${lastSegment}`
          } else if (menu.menuType === 'CONTENT' && lastSegment) {
            // menuLink가 contsId 혹은 "/.../contsId"
            path = `/homepage-mng/contents/view/${lastSegment}`
          }
          // 그 외 타입(INPRG, LINK 등)은 원래 menuLink 그대로 사용

          return {
            ...menu,
            path, // 전역 네비/라우터에서 사용할 실제 이동 경로
          }
        })

        setNavMenus(mapped)
        return mapped
      }
      setNavMenus([])
      return []
    } catch (err) {
      setNavMenus([])
      throw err
    }
  }

  /**
   * [관리 화면용]
   * 도메인 ID로 메뉴 목록 호출 (그리드/검색용)
   * - 전역 네비(navMenusRaw)는 건드리지 않음
   */
  const loadMenuData = async (dmnId, extra = {}) => {
    try {
      const menuReq = { dmnId, searchText: '', ...extra }
      const res = await getMenuList(menuReq)

      if (res?.result && Array.isArray(res.data)) {
        const mapped = res.data.map(m => ({
          ...m,
          dmnId: m.dmnId ?? dmnId
        }))

        manageMenusRaw.value = mapped
        return mapped
      }

      manageMenusRaw.value = []
      return []
    } catch (err) {
      manageMenusRaw.value = []
      throw err
    }
  }


  /* --------------------------------
   * 3. 트리/검색 유틸 (전역 네비 기준)
   * -------------------------------- */

  // 전체 트리 (도메인 무관)
  const getFullTree = () => buildMenuTree(menusWithLevel.value)

  // 도메인 + useYn=Y 기준 트리
  const getMenuTreeByDomain = (dmnId = null) => {
    const base = dmnId
      ? menusWithLevel.value.filter(
        (m) =>
          m.dmnId === dmnId &&
          (m.useYn === 'Y' || m.useYn === 'YES')
      )
      : menusWithLevel.value.filter(
        (m) => m.useYn === 'Y' || m.useYn === 'YES'
      )

    return buildMenuTree(base)
  }

  // 도메인 기준 트리 (useYn 여부 무시)
  const getMenuTree = (dmnId = null) => {
    const base = dmnId
      ? menusWithLevel.value.filter((m) => m.dmnId === dmnId)
      : menusWithLevel.value
    return buildMenuTree(base)
  }

  // 1차 메뉴 (루트 메뉴) 목록
  const getRootMenus = (dmnId = null) => {
    let roots = menusWithLevel.value.filter((menu) => menu.menuLevel === 1)

    if (dmnId) {
      const filtered = roots.filter((menu) => menu.dmnId === dmnId)
      if (filtered.length > 0) roots = filtered
    }

    return roots.sort((a, b) => (a.sortSeq || 0) - (b.sortSeq || 0))
  }

  // 특정 메뉴의 하위 트리 가져오기
  const getSubTreeByParent = (parentMenuId) => {
    if (!parentMenuId) return []

    const fullTree = buildMenuTree(menusWithLevel.value)

    const findNode = (nodes, targetId) => {
      for (const node of nodes) {
        if (node.menuId === targetId) return node
        if (node.children && node.children.length > 0) {
          const found = findNode(node.children, targetId)
          if (found) return found
        }
      }
      return null
    }

    const parentNode = findNode(fullTree, parentMenuId)
    return parentNode?.children ?? []
  }

  // breadcrumb
  const getMenuBreadcrumb = (menuId) =>
    getMenuPath(menusWithLevel.value, menuId)

  // 도메인 + useYn=Y 필터
  const getMenusByDomain = (dmnId) => {
    if (!dmnId) return menusWithLevel.value
    return menusWithLevel.value.filter(
      (menu) =>
        menu.dmnId === dmnId &&
        (menu.useYn === 'Y' || menu.useYn === 'YES')
    )
  }

  // ID로 단일 메뉴 찾기
  const findMenuById = (menuId) =>
    menusWithLevel.value.find((menu) => menu.menuId === menuId) || null

  // 부모 ID로 바로 아래 자식 메뉴들 가져오기
  const getChildMenus = (parentMenuId) =>
    menusWithLevel.value.filter((menu) => menu.upMenuId === parentMenuId)

  // 사용중인 도메인 목록
  const getDomains = () => {
    const domains = new Set()
    menusWithLevel.value.forEach((menu) => {
      if (
        menu.dmnId &&
        (menu.useYn === 'Y' || menu.useYn === 'YES')
      ) {
        domains.add(menu.dmnId)
      }
    })
    return Array.from(domains)
  }

  // 메뉴 검색 (전역 네비 기준)
  const searchMenus = (searchTerm, dmnId = null) => {
    if (!searchTerm) return []
    const term = String(searchTerm).toLowerCase()

    let results = menusWithLevel.value.filter((menu) => {
      if (!(menu.useYn === 'Y' || menu.useYn === 'YES')) return false

      const nm = menu.menuNm?.toLowerCase() || ''
      const subttl = menu.menuSubttl?.toLowerCase() || ''
      const id = menu.menuId?.toLowerCase() || ''

      return (
        nm.includes(term) ||
        subttl.includes(term) ||
        id.includes(term)
      )
    })

    if (dmnId) {
      results = results.filter((menu) => menu.dmnId === dmnId)
    }

    return results.sort((a, b) => (a.sortSeq || 0) - (b.sortSeq || 0))
  }

/* --------------------------------
 * 3-1. 관리 화면용 트리/유틸 (manageMenusRaw 기준)
 * -------------------------------- */

  // 관리용 전체 트리 (도메인 필터 포함)
  const getManageMenuTree = (dmnId = null) => {
    const base = dmnId
      ? manageMenusRaw.value.filter(m => m.dmnId === dmnId)
      : manageMenusRaw.value

    return buildMenuTree(base)
  }

  // 관리용: 부모 기준 하위 메뉴들
  const getManageChildMenus = (parentMenuId) =>
    manageMenusRaw.value.filter(menu => menu.upMenuId === parentMenuId)


  /**
 * 🔹 헤더 1차 메뉴용: 하위 메뉴들 중 "첫 번째 유효한 menuLink" 찾기
 * - 2차 첫 번째에 menuLink가 없으면 그 자식(3차)에서 찾음
 * - 정렬은 buildMenuTree에서 sortSeq 기준으로 되어 있다고 가정
 */
const getFirstNavLink = (rootMenuId) => {
  if (!rootMenuId) return null

  // 전체 트리 한 번 구성
  const fullTree = buildMenuTree(menusWithLevel.value)

  const findNode = (nodes, targetId) => {
    for (const node of nodes) {
      if (node.menuId === targetId) return node
      if (node.children && node.children.length > 0) {
        const found = findNode(node.children, targetId)
        if (found) return found
      }
    }
    return null
  }

  const rootNode = findNode(fullTree, rootMenuId)
  if (!rootNode || !rootNode.children || rootNode.children.length === 0) {
    return null
  }

  // DFS/BFS 상관없지만, "첫 번째" 개념을 위해 순서대로만 보면 됨
  const searchFirstLink = (nodes) => {
    for (const node of nodes) {
      // dir 이 아니고, menuLink 가 있는 경우만 유효한 링크로 간주
      if (node.menuLink && node.menuLink !== 'dir') {
        return node.menuLink
      }
      if (node.children && node.children.length > 0) {
        const childResult = searchFirstLink(node.children)
        if (childResult) return childResult
      }
    }
    return null
  }

  return searchFirstLink(rootNode.children)
}

  return {
    // state
    navMenusRaw,
    manageMenusRaw,
    menusWithLevel, // 전역 네비 기준 (useCurrentMenu 등은 이걸 봄)
    manageMenus,    // 관리 화면 전용 목록
    menuStats,

    // load
    loadAllMenuData, // 전역 네비용
    loadMenuData,    // 메뉴관리 화면용

    // tree (전역 네비 기준)
    getFullTree,
    getMenuTreeByDomain,
    getMenuTree,
    getRootMenus,
    getSubTreeByParent,

    // util (전역 네비 기준)
    getMenuBreadcrumb,
    getMenusByDomain,
    findMenuById,
    getChildMenus,
    getDomains,
    searchMenus,

    // 관리 화면용 트리/유틸
    getManageMenuTree,
    getManageChildMenus,

    getFirstNavLink
  }
}
